/**
 * Core Web Vitals Monitoring Utility
 *
 * Provides comprehensive monitoring of Core Web Vitals metrics:
 * - LCP (Largest Contentful Paint)
 * - FID (First Input Delay)
 * - CLS (Cumulative Layout Shift)
 * - TTFB (Time to First Byte)
 * - INP (Interaction to Next Paint) - Future metric
 */

class CoreWebVitalsMonitor {
  constructor(options = {}) {
    this.options = {
      reportToPostHog: true,
      reportToConsole: false,
      reportToCustomEndpoint: null,
      customEndpoint: null,
      ...options,
    };

    this.metrics = {
      lcp: { value: 0, rating: 'unknown', entry: null },
      fid: { value: 0, rating: 'unknown' },
      cls: { value: 0, rating: 'unknown', entries: [] },
      ttfb: { value: 0, rating: 'unknown' },
      inp: { value: 0, rating: 'unknown' },
    };

    this.observers = new Map();
    this.isObserving = false;

    this.init();
  }

  init() {
    if (typeof PerformanceObserver === 'undefined') {
      console.warn('Core Web Vitals monitoring not supported in this browser');

      return;
    }

    this.setupObservers();

    // Only continue if setup was successful
    if (this.isObserving) {
      this.observeMetrics();
      this.setupVisibilityChangeHandler();
    }
  }

  setupObservers() {
    try {
      // LCP observer
      this.observers.set(
        'lcp',
        new PerformanceObserver(list => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];

          if (lastEntry) {
            this.metrics.lcp.value = lastEntry.startTime;
            this.metrics.lcp.rating = this.getRating('lcp', lastEntry.startTime);
            this.reportMetric('LCP', lastEntry.startTime, this.metrics.lcp.rating);
          }
        })
      );

      // FID observer
      this.observers.set(
        'fid',
        new PerformanceObserver(list => {
          const entries = list.getEntries();

          for (const entry of entries) {
            if (entry.processingStart && entry.startTime) {
              const fid = entry.processingStart - entry.startTime;

              this.metrics.fid.value = fid;
              this.metrics.fid.rating = this.getRating('fid', fid);
              this.reportMetric('FID', fid, this.metrics.fid.rating);
            }
          }
        })
      );

      // CLS observer
      this.observers.set(
        'cls',
        new PerformanceObserver(list => {
          const entries = list.getEntries();

          for (const entry of entries) {
            if (!entry.hadRecentInput) {
              this.metrics.cls.value += entry.value;
              this.metrics.cls.rating = this.getRating('cls', this.metrics.cls.value);
              this.reportMetric('CLS', this.metrics.cls.value, this.metrics.cls.rating);
            }
          }
        })
      );

      // INP observer (for future use)
      this.observers.set(
        'inp',
        new PerformanceObserver(list => {
          const entries = list.getEntries();

          for (const entry of entries) {
            if (entry.interactionId) {
              this.metrics.inp.value = entry.duration;
              this.metrics.inp.rating = this.getRating('inp', entry.duration);
              this.reportMetric('INP', entry.duration, this.metrics.inp.rating);
            }
          }
        })
      );

      this.isObserving = true;
    } catch (error) {
      console.warn('Failed to setup Core Web Vitals observers:', error);
      this.isObserving = false;
    }
  }

  observeMetrics() {
    try {
      for (const [metric, observer] of this.observers) {
        if (metric === 'lcp') {
          observer.observe({ entryTypes: ['largest-contentful-paint'] });
        } else if (metric === 'fid') {
          observer.observe({ entryTypes: ['first-input'] });
        } else if (metric === 'cls') {
          observer.observe({ entryTypes: ['layout-shift'] });
        } else if (metric === 'inp') {
          observer.observe({ entryTypes: ['interaction'] });
        }
      }

      // Only set to true if we're still supposed to be observing
      if (this.isObserving !== false) {
        this.isObserving = true;
      }
    } catch (e) {
      console.warn('Failed to observe some Core Web Vitals:', e);
      this.isObserving = false;
    }
  }

  setupVisibilityChangeHandler() {
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.reportFinalMetrics();
        }
      });
    }
  }

  getRating(metric, value) {
    const thresholds = {
      lcp: { good: 2500, needsImprovement: 4000 },
      fid: { good: 100, needsImprovement: 300 },
      cls: { good: 0.1, needsImprovement: 0.25 },
      inp: { good: 200, needsImprovement: 500 },
      ttfb: { good: 800, needsImprovement: 1800 },
    };

    const metricThresholds = thresholds[metric];

    if (!metricThresholds) {
      return 'unknown';
    }

    if (value <= metricThresholds.good) {
      return 'good';
    }
    if (value <= metricThresholds.needsImprovement) {
      return 'needs-improvement';
    }

    return 'poor';
  }

  reportMetric(metricName, value, rating) {
    const data = {
      metric_name: metricName,
      value: Math.round(value * 100) / 100,
      rating: rating,
      timestamp: Date.now(),
    };

    // Report to PostHog if enabled
    if (this.options.reportToPostHog && typeof window !== 'undefined' && window.posthog) {
      try {
        window.posthog.capture('web_vitals', data);
      } catch (error) {
        console.warn('Failed to send Core Web Vitals to PostHog:', error);
      }
    }

    // Report to console if enabled
    if (this.options.reportToConsole) {
      console.log(`Core Web Vitals - ${metricName}:`, data);
    }

    // Report to custom endpoint if configured
    if (this.options.reportToCustomEndpoint && this.options.customEndpoint) {
      this.sendToCustomEndpoint(data);
    }

    // Dispatch custom event
    if (typeof window !== 'undefined' && window.CustomEvent) {
      try {
        const event = new CustomEvent('web-vitals', { detail: data });

        window.dispatchEvent(event);
      } catch (error) {
        console.warn('Failed to dispatch web-vitals event:', error);
      }
    }
  }

  async sendToCustomEndpoint(data) {
    try {
      const response = await fetch(this.options.customEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.warn('Failed to send Core Web Vitals to custom endpoint:', error);
    }
  }

  reportFinalMetrics() {
    // Calculate TTFB from navigation timing
    if (typeof performance !== 'undefined' && performance.getEntriesByType) {
      try {
        const navigationEntries = performance.getEntriesByType('navigation');

        if (navigationEntries.length > 0) {
          const navigation = navigationEntries[0];
          const ttfb = navigation.responseStart - navigation.requestStart;

          if (ttfb > 0) {
            this.metrics.ttfb.value = ttfb;
            this.metrics.ttfb.rating = this.getRating('ttfb', ttfb);
            this.reportMetric('TTFB', ttfb, this.metrics.ttfb.rating);
          }
        }
      } catch (error) {
        console.warn('Failed to calculate TTFB:', error);
      }
    }

    // Report all final metrics
    for (const [metricName, metric] of Object.entries(this.metrics)) {
      if (metric.value != null) {
        this.reportMetric(metricName, metric.value, metric.rating);
      }
    }
  }

  getMetrics() {
    return { ...this.metrics };
  }

  getMetric(metricName) {
    return this.metrics[metricName.toLowerCase()] || null;
  }

  disconnect() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    this.isObserving = false;
  }
}

// Export for ES modules
export default CoreWebVitalsMonitor;

// Also export for CommonJS
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CoreWebVitalsMonitor;
}
